import React from 'react';
import { AppCriteriaField, AppSkinProps } from '../../../types/decisions';
import { getFieldName, transformCriteriaObjectToArray } from '../../../utils';
import { Button } from '@mui/material';
import AppCriteriaTypeText from './CriteriaFields/AppCriteriaTypeText';
import AppCriteriaTypeNumber from './CriteriaFields/AppCriteriaTypeNumber';
import AppCriteriaTypeSelect from './CriteriaFields/AppCriteriaTypeSelect';
import AppCriteriaTypeZip from './CriteriaFields/AppCriteriaTypeZip';
import AppCriteriaTypeGeo from './CriteriaFields/AppCriteriaTypeGeo';
import AppCriteriaTypeDate from './CriteriaFields/AppCriteriaTypeDate';
import AppCriteriaTypeColor from './CriteriaFields/AppCriteriaTypeColor';
import { useAppConfig } from '../../../context/app-configs-context';
import NameWithIcon from './NameWithIcon/NameWithIcon';

interface Props {
    onSliderChange: AppSkinProps['onSliderChange'];
    onTradeoffValueChange: AppSkinProps['onTradeoffValueChange'];
    fieldInitialValues: AppSkinProps['fieldInitialValues'];
    field: AppCriteriaField,
    areCTAsDisplayed?: boolean;
    handleModalClose?: () => void;
    displayFullWidth?: boolean;
    openCriteriaSettings?: (id: string) => void;
    disabled?: boolean;
}

const ModalCTAs = ({ onSave }: {
    onSave: () => void;
    onCancel: () => void;
}) => {
    return (
        <div className="custom-modal-content-ctas" style={{ textAlign: 'right' }}>
            {/*<Button variant='outlined' onClick={onCancel}>*/}
            {/*    Cancel*/}
            {/*</Button>*/}
            <Button variant="contained" onClick={onSave} style={{ marginLeft: 16 }}>
                Close
            </Button>
        </div>
    );
};

const CriteriaSetting = ({
    field,
    onSliderChange,
    onTradeoffValueChange,
    fieldInitialValues,
    areCTAsDisplayed = false,
    handleModalClose,
    displayFullWidth = false,
    openCriteriaSettings,
    disabled = false,
}: Props) => {
    const { appConfigs: { criteriaSettings } } = useAppConfig();
    let settingContent: React.JSX.Element;

    switch (field.type) {
        case 'text':
        case 'dynamic':
            settingContent = (
                <AppCriteriaTypeText
                    value={field.val as string}
                    hideImportance={true}
                    importance={+field.importance}
                    onSliderChange={onSliderChange}
                    onTradeoffValueChange={onTradeoffValueChange}
                    criteriaConstraints={{}}
                    setCriteriaConstraints={() => {
                    }}
                    id={field.id}
                    disabled={disabled}
                />
            );
            break;
        case 'number':
            let numberValue: number | number[] = +field.val;
            if (field.style === 'range') {
                const rangeValues = field.val as unknown as string[] || [ '0', '0' ];
                numberValue = [ +rangeValues[0], +rangeValues[1] ];
            }

            settingContent = (
                <AppCriteriaTypeNumber
                    value={numberValue}
                    importance={+field.importance}
                    min={+field.min}
                    max={+field.max}
                    hideImportance={true}
                    onSliderChange={onSliderChange}
                    onTradeoffValueChange={onTradeoffValueChange}
                    criteriaConstraints={{}}
                    setCriteriaConstraints={() => {
                    }}
                    id={field.id}
                    variant={criteriaSettings.number}
                    isRange={field.style === 'range'}
                    disabled={disabled}
                />
            );
            break;
        case 'multiselect':
        case 'select':
        case 'bool': {
            const fieldValue: any = fieldInitialValues.find(item => item.id === field.id)?.val;
            settingContent = (
                <AppCriteriaTypeSelect
                    items={transformCriteriaObjectToArray(field.val, field.images, field.link)}
                    importance={+field.importance}
                    hideImportance={true}
                    onSliderChange={onSliderChange}
                    onTradeoffValueChange={onTradeoffValueChange}
                    initialValues={(
                        fieldValue || {}
                    ) as {
                        [key: string]: string
                    }}
                    criteriaConstraints={{}}
                    setCriteriaConstraints={() => {
                    }}
                    id={field.id}
                    variant={criteriaSettings.enumerated}
                    isModal={areCTAsDisplayed}
                    onCriteriaSettingsOpen={openCriteriaSettings}
                    disabled={disabled}
                />
            );
            break;
        }
        case 'zipcode':
            settingContent = (
                <AppCriteriaTypeZip
                    id={field.id}
                    importance={+field.importance}
                    hideImportance={true}
                    onSliderChange={onSliderChange}
                    onTradeoffValueChange={onTradeoffValueChange}
                    variant={criteriaSettings.zipcode}
                    value={field.val as unknown as string[]}
                    disabled={disabled}
                />
            );
            break;
        case 'geoloc':
            settingContent = (
                <AppCriteriaTypeGeo
                    id={field.id}
                    importance={+field.importance}
                    hideImportance={true}
                    onSliderChange={onSliderChange}
                    onTradeoffValueChange={onTradeoffValueChange}
                    variant={criteriaSettings.longLat}
                    value={field.val}
                    displayFullWidth={displayFullWidth}
                    disabled={disabled}
                />
            );
            break;
        case 'date':
            settingContent = (
                <AppCriteriaTypeDate
                    id={field.id}
                    importance={+field.importance}
                    hideImportance={true}
                    onSliderChange={onSliderChange}
                    onTradeoffValueChange={onTradeoffValueChange}
                    value={field.val as string}
                    disabled={disabled}
                />
            );
            break;
        case 'color':
            settingContent = (
                <AppCriteriaTypeColor
                    id={field.id}
                    importance={+field.importance}
                    hideImportance={true}
                    onSliderChange={onSliderChange}
                    onTradeoffValueChange={onTradeoffValueChange}
                    value={field.val as unknown as {
                        color: string;
                        isSelected: boolean;
                    }[]}
                    variant={criteriaSettings.color}
                    disabled={disabled}
                />
            );
            break;
        default:
            settingContent = (
                <span>Unrecognized setting type.</span>
            );
            break;
    }

    const onCancel = () => {
        handleModalClose?.();
    };


    const onSave = () => {
        handleModalClose?.();
    };

    return (
        <div className="custom-modal-content">
            <div key={field.id} className="results-app-settings-item">
                <div className="left">
                    <div className="title">
                        <NameWithIcon icon={field.icon} name={getFieldName(field.name)} link={field.link}/>
                    </div>
                </div>
                <div className="right">
                    {settingContent}
                </div>
                {areCTAsDisplayed ? <ModalCTAs onSave={onSave} onCancel={onCancel}/> : null}
            </div>
        </div>
    );
};

export default CriteriaSetting;